import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import { LoadingScreen } from './components/UI';
import './index.css';

import { SignUp, SignIn, ForgotPassword, ResetPassword, AcceptInvite } from './pages/Auth';
import { EmployeeDashboard, EmployeeSchedule, AvailableShifts, MyRequests, EmpNotifications, EmployeeAvailability } from './pages/EmployeePages';
import { ManagerDashboard, ScheduleBuilder, ManagerRequests, ManagerEmployees, ManagerNotifications, OrgHierarchy, Analytics, ScheduleHistory, TeamNeeds, ManagerAvailability, DepartmentManager } from './pages/ManagerPages';
import Billing from './pages/Billing';
import Landing from './pages/Landing';
import { BlogList, BlogPost } from './pages/Blog';
import Onboarding from './pages/Onboarding';
import ProfileSettings from './pages/ProfileSettings';

// ── Protected layout ──────────────────────────────────────────────────────────
function AppLayout({ children, requireRole }) {
  const { isAuthenticated, isManager, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) { navigate('/login', { replace: true }); return; }
    if (requireRole === 'manager' && !isManager) {
      navigate('/dashboard', { replace: true });
    } else if (requireRole === 'employee' && isManager) {
      navigate('/manager/dashboard', { replace: true });
    }
  }, [isAuthenticated, isManager, loading, navigate, requireRole]);

  if (loading) return <LoadingScreen message="Loading ScheduForge…" />;
  if (!isAuthenticated) return null;

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">{children}</main>
    </div>
  );
}

// ── Root: landing for guests, dashboard redirect for authenticated users ───────
function Root() {
  const { isAuthenticated, isManager, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Landing />;
  return <Navigate to={isManager ? '/manager/dashboard' : '/dashboard'} replace />;
}

// ── Routes ────────────────────────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/signup"          element={<SignUp />} />
      <Route path="/login"           element={<SignIn />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/invite"          element={<AcceptInvite />} />

      {/* Employee */}
      <Route path="/dashboard"    element={<AppLayout requireRole="employee"><EmployeeDashboard /></AppLayout>} />
      <Route path="/schedule"     element={<AppLayout requireRole="employee"><EmployeeSchedule /></AppLayout>} />
      <Route path="/available"    element={<AppLayout requireRole="employee"><AvailableShifts /></AppLayout>} />
      <Route path="/requests"     element={<AppLayout requireRole="employee"><MyRequests /></AppLayout>} />
      <Route path="/notifications" element={<AppLayout requireRole="employee"><EmpNotifications /></AppLayout>} />
      <Route path="/availability"  element={<AppLayout requireRole="employee"><EmployeeAvailability /></AppLayout>} />
      <Route path="/profile"       element={<AppLayout requireRole="employee"><ProfileSettings /></AppLayout>} />

      {/* Manager */}
      <Route path="/manager/dashboard"     element={<AppLayout requireRole="manager"><ManagerDashboard /></AppLayout>} />
      <Route path="/manager/my-schedule"   element={<AppLayout requireRole="manager"><EmployeeSchedule /></AppLayout>} />
      <Route path="/manager/schedule"      element={<AppLayout requireRole="manager"><ScheduleBuilder /></AppLayout>} />
      <Route path="/manager/requests"      element={<AppLayout requireRole="manager"><ManagerRequests /></AppLayout>} />
      <Route path="/manager/employees"     element={<AppLayout requireRole="manager"><ManagerEmployees /></AppLayout>} />
      <Route path="/manager/notifications" element={<AppLayout requireRole="manager"><ManagerNotifications /></AppLayout>} />
      <Route path="/manager/availability"  element={<AppLayout requireRole="manager"><ManagerAvailability /></AppLayout>} />
      <Route path="/manager/team-needs"    element={<AppLayout requireRole="manager"><TeamNeeds /></AppLayout>} />
      <Route path="/manager/analytics"     element={<AppLayout requireRole="manager"><Analytics /></AppLayout>} />
      <Route path="/manager/history"       element={<AppLayout requireRole="manager"><ScheduleHistory /></AppLayout>} />
      <Route path="/billing"               element={<AppLayout requireRole="manager"><Billing /></AppLayout>} />
      <Route path="/owner/hierarchy"        element={<AppLayout requireRole="manager"><OrgHierarchy /></AppLayout>} />
      <Route path="/manager/profile"        element={<AppLayout requireRole="manager"><ProfileSettings /></AppLayout>} />
      <Route path="/manager/departments"    element={<AppLayout requireRole="manager"><DepartmentManager /></AppLayout>} />

      {/* Onboarding (owner-only post-signup wizard) */}
      <Route path="/onboarding" element={<Onboarding />} />

      {/* Blog */}
      <Route path="/blog"       element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* Catch-all */}
      <Route path="/"   element={<Root />} />
      <Route path="*"   element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
